import classNames from 'classnames';
import { FC, useMemo } from 'react';

import { Locale, useLocale } from '../../helpers/LangHelper';
import * as style from '../../styles/generic.module.scss';
import BusinessFooter from '../business-footer/business-footer';
import { BusinessHubFooter } from '../business-hub-footer/business-hub-footer';
import BusinessHubFootnotes from '../business-hub-footnotes/business-hub-footnotes';
import { BusinessHubTopNavigation } from '../business-hub-navigation/business-hub-top-navigation';

interface Props {
    page: any;
}

const BusinessHubLayout: FC<Props> = (props) => {
    const { page, children } = props;

    const { footerItems, theme, footnotes, hideFooter, hideNav, showContactUsForm, showSayHello, showReadyToGrow } = page?.data || {};

    const { locale } = useLocale();

    const isANZ = locale === Locale.Australia || locale === Locale.NewZealand;

    const localeName = useMemo(() => {
        switch (locale) {
            case Locale.Australia:
            case Locale.NewZealand:
                return 'Australia';
            case Locale.USA:
            case Locale.CanadaEnglish:
                return 'United States';
            case Locale.GreatBritain:
                return 'United Kingdom';
            default:
                return null;
        }
    }, [locale]);

    const copyright = useMemo(() => {
        switch (locale) {
            case Locale.Australia:
            case Locale.NewZealand:
                return '© 2024 Afterpay Australia Pty Ltd.';
            case Locale.USA:
            case Locale.CanadaEnglish:
                return '© 2024 Afterpay';
            case Locale.GreatBritain:
                return '© 2024 Clearpay';
            default:
                return null;
        }
    }, [locale]);

    const disclaimers = useMemo(() => {
        switch (locale) {
            case Locale.Australia:
            case Locale.NewZealand:
                return (
                    <>
                        Afterpay acknowledges the Australian Aboriginal and Torres Strait Islander peoples as the traditional custodians of the land, sea and waters where we live,
                        meet and work. We pay our respects to their Elders past and present. <strong>Always was, always will be, Aboriginal Land.</strong>
                    </>
                );
            case Locale.GreatBritain:
                return (
                    <>
                        Clearpay lends you a fixed amount of credit so you can pay for your purchase over 4 instalments, due every 2 weeks. Ensure you can make repayments on time.
                        You must be 18+ and a permanent UK resident (excl Channel Islands). Clearpay charges a £6 late fee for each late instalment and a further £6 if it’s still
                        unpaid 7 days later. Late fees are capped at £6 for orders under £24 and the lower of £24 or 25% of the order value for orders over £24. Missed payments may
                        affect your ability to use Clearpay in the future and your details may be passed onto a debt collection agency working on Clearpay's behalf. Clearpay is
                        credit that is not regulated by the Financial Conduct Authority. T&Cs and other eligibility criteria apply at{' '}
                        <a href="https://www.clearpay.co.uk/en-GB/terms-of-service" target="_blank">
                            https://www.clearpay.co.uk/en-GB/terms-of-service
                        </a>
                        .
                    </>
                );
            default:
                return null;
        }
    }, [locale]);

    const onboardingLink = useMemo(() => {
        switch (locale) {
            case Locale.GreatBritain:
                return 'https://hub.clearpay.co.uk/signup';
            case Locale.USA:
                return 'https://hub.us.afterpay.com/signup';
            case Locale.CanadaEnglish:
            case Locale.CanadaFrench:
                return 'https://hub.us.afterpay.com/signup?country=ca';
            case Locale.Australia:
                return 'https://hub.afterpay.com/signup?country=au';
            case Locale.NewZealand:
                return 'https://hub.afterpay.com/signup?country=nz';
            default:
                return 'https://hub.afterpay.com/signup';
        }
    }, [locale]);

    const LocaleIcon = localeName ? (
        <div className={(style as any)['locale']}>
            <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M11.2222 1.02319V2.78424C13.7256 3.35398 15.7715 5.14095 16.7038 7.48034C15.055 6.63436 12.7156 6.15092 10.3589 6.04733V1.71383C10.3589 1.71383 10.3589 1.71382 10.3589 1.70519V0.89379C10.0741 0.867893 9.78918 0.850586 9.49568 0.850586C4.25579 0.850586 0 5.10638 0 10.3463C0 14.9905 3.35802 18.8579 7.76919 19.678V17.9169C5.26579 17.3472 3.21989 15.5602 2.28759 13.2208C3.93639 14.0668 6.28442 14.5589 8.64108 14.6538V18.0723V19.8074C8.92595 19.8333 9.21082 19.8506 9.50432 19.8506C14.7442 19.8506 19 15.5948 19 10.3549C19 5.71066 15.642 1.84327 11.2308 1.02319H11.2222ZM17.2649 10.3549C17.2649 11.3476 14.6061 12.7547 10.3589 12.9274V7.78246C14.5974 7.95511 17.2649 9.36218 17.2649 10.3549ZM8.63244 2.63756V6.05597C6.27578 6.15093 3.92777 6.64301 2.27898 7.48898C3.3235 4.87336 5.74057 2.95696 8.63244 2.63756ZM1.72649 10.3549C1.72649 9.36218 4.39391 7.95511 8.63244 7.78246V12.9274C4.38528 12.7547 1.72649 11.3476 1.72649 10.3549ZM10.3589 18.0723V14.6538C12.7156 14.5589 15.055 14.0668 16.7038 13.2208C15.6592 15.8278 13.2508 17.7442 10.3589 18.0723Z"
                    fill="black"
                />
            </svg>
            <span>
                <strong>{localeName}</strong>
            </span>
        </div>
    ) : null;

    return (
        <div className={(style as any)['business-hub-layout']}>
            {hideNav ? null : <BusinessHubTopNavigation page={page} signupLink={onboardingLink} />}

            <div className={classNames((style as any)['content'], (style as any)['--no-top-padding'])}>{children}</div>

            <div className={(style as any)['footer']}>
                {hideFooter ? null : (
                    <BusinessHubFooter
                        theme={theme}
                        showContactUsForm={showContactUsForm}
                        showSayHello={showSayHello}
                        showReadyToGrow={showReadyToGrow}
                        footerLink={onboardingLink}
                        page={page}
                    />
                )}
                <BusinessHubFootnotes content={footnotes} />

                {LocaleIcon ? (
                    <div className={classNames((style as any)['disclaimers-box'], (style as any)['mobile'])}>
                        {LocaleIcon}
                        <svg width="34" height="30" viewBox="0 0 34 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M30.8531 5.94546L26.2373 3.30405L21.5548 0.62262C18.4598 -1.15166 14.5911 1.0762 14.5911 4.65144V5.25175C14.5911 5.58526 14.7645 5.8921 15.058 6.05218L17.2325 7.29284C17.8328 7.6397 18.5799 7.19946 18.5799 6.50576V5.07833C18.5799 4.37129 19.3403 3.93105 19.954 4.2779L24.2229 6.73254L28.4785 9.17385C29.0922 9.5207 29.0922 10.4145 28.4785 10.7614L24.2229 13.2027L19.954 15.6573C19.3403 16.0042 18.5799 15.5639 18.5799 14.8569V14.1498C18.5799 10.5746 14.7112 8.3334 11.6162 10.121L6.94704 12.8158L2.33124 15.4572C-0.777081 17.2448 -0.777081 21.7405 2.33124 23.5282L6.94704 26.1696L11.6295 28.851C14.7245 30.6253 18.5933 28.3974 18.5933 24.8222V24.2219C18.5933 23.8884 18.4198 23.5815 18.1263 23.4214L15.9518 22.1808C15.3515 21.8339 14.6045 22.2742 14.6045 22.9679V24.3953C14.6045 25.1023 13.8441 25.5426 13.2304 25.1957L8.96145 22.7411L4.70584 20.2998C4.09218 19.9529 4.09218 19.0591 4.70584 18.7123L8.96145 16.271L13.2304 13.8163C13.8441 13.4695 14.6045 13.9097 14.6045 14.6168V15.3238C14.6045 18.899 18.4732 21.1402 21.5682 19.3526L26.2507 16.6712L30.8665 14.0298C33.9614 12.2288 33.9614 7.73308 30.8531 5.94546Z"
                                fill="black"
                            />
                        </svg>
                    </div>
                ) : null}

                <div className={localeName ? (style as any)['footer-nav'] : ''}>
                    <BusinessFooter theme="white" hideLogo footerData={footerItems} isB2bAnz={isANZ} />
                </div>

                {localeName ? (
                    <div className={(style as any)['disclaimers-box']}>
                        <div className={(style as any)['desktop']}>{LocaleIcon}</div>
                        {copyright ? <div className={(style as any)['copyright']}>{copyright}</div> : null}
                        {disclaimers ? <div className={(style as any)['disclaimers']}>{disclaimers}</div> : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default BusinessHubLayout;
